import { useState, useEffect, useCallback } from "react";
import { Download } from "lucide-react";
import axios from "axios";
import { v2SubscriptionsApi } from "api";
import { DataTable } from "components";
import { Check, Clock, X } from "lucide-react";

const PaymentsTab = ({ onError, onSuccess }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      const response = await v2SubscriptionsApi.getPayments({ page: 1 });
      setPayments(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error("Error loading payments:", err);
      onError(err.response?.data?.error || "Error cargando historial de pagos");
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const response = await axios.get("/api/payments/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `historial-pagos-${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onSuccess("Historial de pagos exportado con éxito");
    } catch (err) {
      console.error("Error exporting payments:", err);
      onError(err.response?.data?.error || "Error exportando historial de pagos");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      key: "created_at",
      label: "Fecha",
      render: (value) => new Date(value).toLocaleDateString(),
    },
    { key: "plan_name", label: "Plan" },
    {
      key: "amount",
      label: "Monto",
      render: (value) => `$${value.toFixed(2)}`,
    },
    {
      key: "status",
      label: "Estado",
      render: (value) => {
        const base = "inline-flex items-center px-3 py-1 rounded-full text-xs font-medium backdrop-blur-md border";
    
        const variants = {
          completed: {
            label: "Completado",
            icon: <Check className="w-3 h-3 mr-1" />,
            className: "bg-green-500/20 border-green-400/30 text-green-300",
          },
          approved: {
            label: "Aprobado",
            icon: <Check className="w-3 h-3 mr-1" />,
            className: "bg-green-500/20 border-green-400/30 text-green-300",
          },
          pending: {
            label: "Pendiente",
            icon: <Clock className="w-3 h-3 mr-1" />,
            className: "bg-yellow-500/20 border-yellow-400/30 text-yellow-300",
          },
          failed: {
            label: "Fallido",
            icon: <X className="w-3 h-3 mr-1" />,
            className: "bg-red-500/20 border-red-400/30 text-red-300",
          },
        };
    
        const variant = variants[value] || {
          label: value,
          icon: null,
          className: "bg-white/10 border-white/20 text-white/70",
        };
    
        return (
          <span className={`${base} ${variant.className}`}>
            {variant.icon}
            {variant.label}
          </span>
        );
      },
    },    
    { key: "payment_method", label: "Método" },
  ];

  return (
    <div className="w-full pb-6">
      <div className="backdrop-blur-xl bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 border border-white/20 rounded-2xl shadow-lg overflow-hidden">

        {/* Header Section */}
        <div className="p-4 md:p-6 border-b border-white/10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/40 flex items-center justify-center backdrop-blur-md flex-shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">Historial de Pagos</h3>
                <p className="text-white/60 text-xs md:text-sm">Revisa tu historial de transacciones</p>
              </div>
            </div>

            <button
              onClick={handleExport}
              disabled={exporting}
              className="w-full md:w-auto px-4 py-3 md:py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-xl transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 font-medium text-sm md:text-base"
            >
              <Download className="w-4 h-4" />
              <span>{exporting ? "Exportando..." : "Exportar CSV"}</span>
            </button>
          </div>
        </div>

        {/* DataTable Section */}
        <div className="p-4 md:p-6 overflow-x-auto">
          <DataTable
            columns={columns}
            items={payments}
            loading={loading}
            emptyMessage="No tienes pagos realizados"
          />
        </div>
      </div>
    </div>
  );
};

export default PaymentsTab;